import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect } from 'react';

import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { PlanActionOrgUnitStatus } from 'src/types/planAction';
import type { ActionPlanCacheReturnProps } from 'src/hooks/useActionPlanCache';
import { useActionPlanCache } from 'src/hooks/useActionPlanCache';
import { usePlanActionCache } from 'src/hooks/usePlanActionCache';
import { useUserContext } from 'src/components/app/context/UserContext';
import useOrgTreeState from 'src/components/common/OrgTree/hooks/useOrgTreeState';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';

interface PlanActionStatusValue {
  value: PlanActionOrgUnitStatus | null;
  loading?: boolean;
  error?: string;
}

export interface PlanActionContextValue extends ActionPlanCacheReturnProps {
  planActionStatus: PlanActionStatusValue;
  orgTreeState?: OrgTreeState;
  resetPlanActionContext: () => void;
  selectedOrgUnit?: ModuleOrPlanActionOrgUnit;
}

export const initialContextValue = {
  // PlanActionContextValues
  planActionStatus: { value: null },
  orgTreeState: undefined,
  resetPlanActionContext: () => {},
  selectedOrgUnit: undefined,

  // UseActionPlanCacheValues
  actionPlans: [],
  changeCachedActionPlan: () => {},
  deleteActionPlan: () => {},
  errors: null,
  fetchActionPlansForSelectedOrgUnit: () => {},
  loadingActionPlans: false,
  resetActionPlanCache: () => {},
  resetErrors: () => {},
  saveActionPlan: () => {},

  // TODO: Add if "usePlanActionCache" is typed.
  // UsePlanActionCache
};

export const PlanActionContext =
  createContext<PlanActionContextValue>(initialContextValue);

export interface PlanActionProviderProps {
  children: ReactNode;
}

const PlanActionProvider: FC<PlanActionProviderProps> = ({ children }) => {
  const { user } = useUserContext();

  const orgTreeState = useOrgTreeState();
  const planActionStatus = usePlanActionCache(
    orgTreeState.latestSelectedOrgUnit,
  );

  const actionPlanCache = useActionPlanCache(
    planActionStatus.reloadPlanActionStatus,
    orgTreeState.latestSelectedOrgUnit,
  );

  const resetPlanActionContext = () => {
    planActionStatus.resetPlanActionCache();
    actionPlanCache.resetActionPlanCache();
  };

  useEffect(() => {
    planActionStatus.resetPlanActionCache();
    actionPlanCache.resetActionPlanCache();
  }, [
    user,
    planActionStatus.resetPlanActionCache,
    actionPlanCache.resetActionPlanCache,
  ]);

  return (
    <PlanActionContext.Provider
      value={{
        orgTreeState,
        resetPlanActionContext,
        selectedOrgUnit: orgTreeState.latestSelectedOrgUnit,
        ...planActionStatus,
        ...actionPlanCache,
      }}
    >
      {children}
    </PlanActionContext.Provider>
  );
};

export const usePlanActionContext = () => useContext(PlanActionContext);

export default PlanActionProvider;
